import { AssessmentDetail } from '@api';
import { Icon } from '@components/ui';
import { getMetricIconStatus } from '@data-access';
import { AssessmentsEnum } from '@sections/ModelInventoryTable/types';
import { rankItem } from '@tanstack/match-sorter-utils';
import clsx from 'clsx';
import dayjs from 'dayjs';
import katex from 'katex';
import { capitalize } from 'lodash';
import 'katex/dist/katex.min.css';

import styles from '../../data-access/Status.module.scss';

function replaceItemAt<T>(index: number, newItem: T, array: T[]) {
  return Object.assign([], array, { [index]: newItem });
}

const flattenErrorMessages = (value: any): string[] => {
  if (typeof value === 'string') return [value];
  if (Array.isArray(value)) return value.flatMap(flattenErrorMessages);
  if (value && typeof value === 'object') return Object.values(value).flatMap(flattenErrorMessages);
  return [];
};

const jsonToString = (object: any): string => {
  let strings = '';

  Object.entries(object).forEach((entry: any[]) => {
    const messages = flattenErrorMessages(entry[1]).join(', ');
    if (messages) strings += `${entry[0]}: ${messages}\n`;
  });

  return strings;
};

const getAssessmentStatus = (status: string | undefined) => {
  switch (status) {
    case AssessmentsEnum.running:
      return (
        <div className={styles.statusWrapper}>
          <div className={clsx(styles.statusIcon, styles.running)}>
            <Icon name="arrows-rotate" />
          </div>
          {/* <span>Running</span> */}
        </div>
      );
    case AssessmentsEnum.failed:
      return (
        <div className={styles.statusWrapper}>
          <div className={clsx(styles.statusIcon, styles.failed)}>
            <Icon name="exclamation-triangle" />
          </div>
          {/* <span>Failed</span> */}
        </div>
      );
    case AssessmentsEnum.success:
      return (
        <div className={styles.statusWrapper}>
          <div className={clsx(styles.statusIcon, styles.success)}>
            <Icon name="check" />
          </div>
          {/* <span>Successful</span> */}
        </div>
      );
    case AssessmentsEnum.successWithErrors:
      return (
        <div className={styles.statusWrapper}>
          <div className={clsx(styles.statusIcon, styles.success)}>
            <Icon name="exclamation-triangle" />
          </div>
          {/* <span>Successful with errors</span> */}
        </div>
      );
    case AssessmentsEnum.pending:
      return (
        <div className={styles.statusWrapper}>
          <div className={clsx(styles.statusIcon, styles.pending)}>
            <Icon name="clock" />
          </div>
          {/* <span>Pending</span> */}
        </div>
      );
    default:
      return (
        <div className={styles.statusWrapper}>
          <div className={clsx(styles.statusIcon, styles.notStarted)}>
            <Icon name="dash" />
          </div>
          {/* <span>Not started</span> */}
        </div>
      );
  }
};

const getAssessmentStatusWithName = (status: string | undefined) => {
  switch (status) {
    case AssessmentsEnum.running:
      return (
        <div className={styles.statusWrapper}>
          <div className={clsx(styles.statusIcon, styles.running)}>
            <Icon name="arrows-rotate" />
          </div>
          <span className={styles.infoText}>Running</span>
        </div>
      );
    case AssessmentsEnum.failed:
      return (
        <div className={styles.statusWrapper}>
          <div className={clsx(styles.statusIcon, styles.failed)}>
            <Icon name="exclamation-triangle" />
          </div>
          <span className={styles.infoText}>Failed</span>
        </div>
      );
    case AssessmentsEnum.success:
      return (
        <div className={styles.statusWrapper}>
          <div className={clsx(styles.statusIcon, styles.success)}>
            <Icon name="check" />
          </div>
          <span className={styles.infoText}>Successful</span>
        </div>
      );
    case AssessmentsEnum.successWithErrors:
      return (
        <div className={styles.statusWrapper}>
          <div className={clsx(styles.statusIcon, styles.success)}>
            <Icon name="exclamation-triangle" />
          </div>
          <span className={styles.infoText}>Successful with errors</span>
        </div>
      );
    case AssessmentsEnum.pending:
      return (
        <div className={styles.statusWrapper}>
          <div className={clsx(styles.statusIcon, styles.pending)}>
            <Icon name="clock" />
          </div>
          <span className={styles.infoText}>Pending</span>
        </div>
      );
    default:
      return (
        <div className={styles.statusWrapper}>
          <div className={clsx(styles.statusIcon, styles.notStarted)}>
            <Icon name="dash" />
          </div>
          <span className={styles.infoText}>Not started</span>
        </div>
      );
  }
};

const getColorForThreshold = (threshold, min, max) => {
  const normalized = (threshold - min) / (max - min);

  const lightenAmount = 0.08;
  const lighten = (c: number) => c + (255 - c) * lightenAmount;

  const yellow = [242, 235, 70].map(lighten);
  const green = [136, 206, 110].map(lighten);
  const red = [200, 76, 60].map(lighten);

  const interpolateColors = (color1, color2, factor) => color1.map((c, i) => Math.round(c + (color2[i] - c) * factor));

  let color;
  if (normalized < 0.5) {
    // Between green and yellow
    const factor = normalized * 2;
    color = interpolateColors(green, yellow, factor);
  } else {
    // Between yellow and red
    const factor = (normalized - 0.5) * 2;
    color = interpolateColors(yellow, red, factor);
  }

  return `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
};

const findStatus = (
  assessment: AssessmentDetail,
  metric: string,
  placement?: 'right' | 'left' | 'bottom' | 'top' | 'bottom-start' | 'bottom-end' | 'top-start',
) => {
  if (assessment.status === AssessmentsEnum.running) {
    return getMetricIconStatus({ status: AssessmentsEnum.running, placement });
  }

  const findErrors = assessment.riskIndicator?.errors.find(
    error =>
      // eslint-disable-next-line @typescript-eslint/dot-notation
      error['metric_name'] === metric,
  );

  // eslint-disable-next-line @typescript-eslint/dot-notation
  const findWarnings = assessment.riskIndicator?.warnings.find(warning => warning.metricName === metric);

  if (findErrors) {
    return getMetricIconStatus({ status: AssessmentsEnum.failed, message: findErrors.message, placement });
  }

  if (findWarnings) {
    return getMetricIconStatus({
      status: AssessmentsEnum.successWithWarnings,
      message: findWarnings.message,
      placement,
    });
  }

  return getMetricIconStatus({ status: AssessmentsEnum.success, placement });
};

const getRiskIndicatorLevel = (score: number, limits: number[]) => {
  if (score < 0) {
    return 'negative';
  }
  if (score <= limits[0]) {
    return 'low';
  }
  if (score > limits[0] && score <= limits[1]) {
    return 'medium';
  }
  if (score > limits[1] && score <= limits[2]) {
    return 'high';
  }
  return 'veryhigh';
};

const getRiskIndicatorColor = (score: number, limits: number[]) => {
  if (score === null || score === undefined || Number.isNaN(score)) {
    return '';
  }
  if (score <= limits[0]) {
    return '#5d9f46';
  }
  if (score > limits[0] && score <= limits[1]) {
    return '#e3ba16';
  }
  if (score > limits[1] && score <= limits[2]) {
    return '#e77e23';
  }
  return '#c84c3c';
};

const capitalizeString = (string: string) => `${string.charAt(0).toUpperCase()}${string.slice(1).toLowerCase()}`;

const snakeCaseToWords = (str: string) => {
  if (!str) return '';
  return str
    .split('_')
    .map(word => capitalizeString(word))
    .join(' ');
};

const commaSeparatedToReadableString = (string: string) => {
  if (!string) return '/';
  if (string === 'All') return 'All indicators were assessed';
  try {
    const parsedString = JSON.parse(string);
    const arrayOfString = parsedString.split(',');
    if (arrayOfString.length === 4) return 'All indicators were assessed';
    return arrayOfString
      .map((s: string) => capitalize(s))
      .reduce((prev: string, curr: string, index: number) => {
        if (index === 0) return curr;
        return index !== arrayOfString.length - 1 ? `${prev}, ${curr}` : `${prev} & ${curr}`;
      }, '');
  } catch (error) {
    console.log(error);
  }
  return '';
};

// round numberd
const roundNumber = (number: number, decimals: number = 3, minimumDigits: number = 0) => {
  if (Number.isInteger(number)) {
    return new Intl.NumberFormat('ch-CH', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
      .format(number)
      .replaceAll(',', '’');
  }
  return new Intl.NumberFormat('ch-CH', {
    minimumFractionDigits: minimumDigits,
    maximumFractionDigits: decimals,
  })
    .format(number)
    .replaceAll(',', '’');
};

const fuzzyFilter = (row, columnId, value, addMeta) => {
  // Rank the item
  const itemRank = rankItem(row.getValue(columnId), value);
  // Store the itemRank info
  addMeta({
    itemRank,
  });
  // Return if the item should be filtered in/out
  return itemRank.passed;
};

const parseFileName = (fullName: string): string => {
  const parsedName = fullName.split('?')[0].split('/').pop() || '';

  const dotIdx = parsedName.lastIndexOf('.');
  if (dotIdx === -1) return parsedName;

  const ext = parsedName.slice(dotIdx); // includes the dot
  const base = parsedName.slice(0, dotIdx);

  const hash = base.slice(-8);

  if (hash[0] === '_') {
    return parseFileName(base.slice(0, -8) + ext);
  }

  return parsedName;
};

const getInitials = username => {
  const nameArray = username.split(' ');
  return nameArray
    .map(name => name.charAt(0))
    .join('')
    .toUpperCase();
};

const formatDate = date => dayjs(date).format('MMM D, YYYY');

const formatDateWithHour = date => dayjs(date).format('MMM D, YYYY, h:mm A');

function groupBy<T>(array: T[], keyAccessor: (item: T) => string | number): { [key: string]: T[] } {
  return array.reduce((result, currentValue) => {
    const groupKey = keyAccessor(currentValue);
    return {
      ...result,
      [groupKey]: [...(result[groupKey] || []), currentValue],
    };
  }, {} as { [key: string]: T[] });
}

const stringToHslColor = (str: string, s: number, l: number) => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    /* eslint no-bitwise: "off" */
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const h = hash % 360;
  return `hsl(${h}, ${s}%, ${l}%)`;
};

export const renderContentWithMath = (content: string | undefined | null): string => {
  if (!content) return '';

  const parts = content.split(/(\$[^$]+\$)/);

  return parts
    .map(
      part =>
        part.startsWith('$')
          ? katex.renderToString(part.slice(1, -1), { throwOnError: false }) // math notation
          : part, // plain text
    )
    .join('');
};

/**
 * Applies border radius to chart bars based on data values
 * @param dataArray Array of data values
 * @param radius Border radius size (default: 18)
 * @returns Array of border radius objects
 */
const applyBorderRadius = (dataArray: number[], radius = 8) => {
  const nonZeroIndices = dataArray.map((value, i) => (value > 0 ? i : -1)).filter(i => i !== -1);
  const first = nonZeroIndices[0];
  const last = nonZeroIndices[nonZeroIndices.length - 1];
  return dataArray.map((value, i) => {
    if (value === 0) {
      return 0;
    }
    if (i === first && i === last) {
      return { topLeft: radius, bottomLeft: radius, topRight: radius, bottomRight: radius };
    }
    if (i === first) {
      return { topLeft: radius, bottomLeft: radius };
    }
    if (i === last) {
      return { topRight: radius, bottomRight: radius };
    }
    return 0;
  });
};

export const MapperHelper = {
  getColorForThreshold,
  capitalizeString,
  snakeCaseToWords,
  commaSeparatedToReadableString,
  getAssessmentStatus,
  getAssessmentStatusWithName,
  getRiskIndicatorLevel,
  getRiskIndicatorColor,
  jsonToString,
  replaceItemAt,
  roundNumber,
  fuzzyFilter,
  parseFileName,
  getInitials,
  formatDate,
  formatDateWithHour,
  findStatus,
  groupBy,
  stringToHslColor,
  renderContentWithMath,
  applyBorderRadius,
};
